/** Territory — プレイヤーの領地状態を管理。
 *  Reference: docs/STAGE_DESIGN.md §1.1 territory
 *  - 閉ループ内のマスをすべて自色化
 *  - 占領率 = owned cells / total cells
 *  - enclose() は flood-fill 外部判定で「囲まれた領域」を計算
 *  - 描画は境界トレース + Chaikin 平滑化で領地全体を一つの滑らかな多角形として描く
 */

import { Container, Graphics } from 'pixi.js';
import type { GridConfig, GridPos } from './types';
import { gridIndex, inBounds } from './types';

export type CellState = 0 | 1; // 0 = empty, 1 = owned

interface AnimatingCell {
  pos: GridPos;
  age: number; // negative = pre-wave(波が届くまで待機)
}

interface RippleEntry {
  /** 原点(world pixel) */
  cx: number;
  cy: number;
  age: number;
  duration: number;
  /** 最終半径(px) */
  maxRadius: number;
}

interface AutoExpandConfig {
  intervalSec: number;
  cellsPerTick: number;
  elapsed: number;
}

const FADE_DURATION_SEC = 0.45;
const WAVE_DELAY_PER_CELL = 0.025;
const TRAIL_BRIGHT_ALPHA = 0.55;
const STABLE_ALPHA = 0.32;
const CHAIKIN_ITERATIONS = 3; // 0=矩形 / 1=45° / 2=滑らか / 3=さらに滑らか
const RIPPLE_DURATION_SEC = 0.65;
const RIPPLE_RADIUS_CELLS = 5.6;

export class Territory {
  public readonly view: Container;

  private readonly grid: GridConfig;
  private readonly color: number;
  private readonly graphics: Graphics;
  private cells: Uint8Array;
  private animatingCells: AnimatingCell[] = [];
  private ripples: RippleEntry[] = [];
  private autoExpand: AutoExpandConfig | null = null;

  constructor(grid: GridConfig, color: number) {
    this.grid = grid;
    this.color = color;
    this.cells = new Uint8Array(grid.gridSize * grid.gridSize);

    this.view = new Container();
    this.graphics = new Graphics();
    this.view.addChild(this.graphics);
  }

  /** 全マスを未領地に戻す(ステージリセット時に呼ぶ)。 */
  clear(): void {
    this.cells.fill(0);
    this.animatingCells = [];
    this.ripples = [];
    this.autoExpand = null;
    this.graphics.clear();
  }

  /** Pixi ticker から毎フレーム呼ぶ。波アニメーションを進める。 */
  update(dt: number): void {
    // auto-expand: intervalSec ごとに境界セルから隣接空きマスへ拡張
    if (this.autoExpand !== null) {
      this.autoExpand.elapsed += dt;
      if (this.autoExpand.elapsed >= this.autoExpand.intervalSec) {
        this.autoExpand.elapsed -= this.autoExpand.intervalSec;
        this.tickAutoExpand(this.autoExpand.cellsPerTick);
      }
    }

    if (this.animatingCells.length === 0 && this.ripples.length === 0) return;
    let stillAnimating = false;
    for (const entry of this.animatingCells) {
      entry.age += dt;
      if (entry.age < FADE_DURATION_SEC) stillAnimating = true;
    }
    if (!stillAnimating) {
      this.animatingCells = [];
    } else {
      this.animatingCells = this.animatingCells.filter((e) => e.age < FADE_DURATION_SEC);
    }

    if (this.ripples.length > 0) {
      for (const r of this.ripples) r.age += dt;
      this.ripples = this.ripples.filter((r) => r.age < r.duration);
    }

    this.redraw();
  }

  /** 矩形領域を初期領地化(チュートリアル開始時の中央 5x5 等)。 */
  setRect(x: number, y: number, w: number, h: number): void {
    for (let dy = 0; dy < h; dy++) {
      for (let dx = 0; dx < w; dx++) {
        const pos: GridPos = { x: x + dx, y: y + dy };
        if (inBounds(pos, this.grid.gridSize)) {
          this.cells[gridIndex(pos, this.grid.gridSize)] = 1;
        }
      }
    }
    this.redraw();
  }

  contains(pos: GridPos): boolean {
    if (!inBounds(pos, this.grid.gridSize)) return false;
    return this.cells[gridIndex(pos, this.grid.gridSize)] === 1;
  }

  /** paper.io 式に「ライバル領地が自動でじわじわ拡張する」モードを有効化。
   *  intervalSec ごとに、現在の所有セルのうちランダムな縁セルから
   *  cellsPerTick 個まで隣接の空きマスへ拡張する。
   *  GameplayScreen 側のライバル Territory に対してのみ呼び出される想定。
   *  推奨パラメータ: intervalSec=3.0, cellsPerTick=1(「じわじわ取られる」体感)
   */
  public enableAutoExpand(intervalSec: number, cellsPerTick = 1): void {
    this.autoExpand = { intervalSec, cellsPerTick, elapsed: 0 };
  }

  /** auto-expand の 1 ティック分処理。update() から呼ばれる内部メソッド。 */
  private tickAutoExpand(cellsPerTick: number): void {
    const size = this.grid.gridSize;

    // 境界セル(owned かつ隣接に空きマスが 1 つ以上ある)を収集
    const borderCells: GridPos[] = [];
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const idx = y * size + x;
        if (this.cells[idx] !== 1) continue;
        const neighbors: GridPos[] = [
          { x: x - 1, y },
          { x: x + 1, y },
          { x, y: y - 1 },
          { x, y: y + 1 },
        ];
        for (const n of neighbors) {
          if (!inBounds(n, size)) continue;
          if (this.cells[gridIndex(n, size)] === 0) {
            borderCells.push({ x, y });
            break;
          }
        }
      }
    }

    if (borderCells.length === 0) return;

    for (let tick = 0; tick < cellsPerTick; tick++) {
      // 境界セルからランダムに 1 つ選ぶ
      const borderIdx = Math.floor(Math.random() * borderCells.length);
      const origin = borderCells[borderIdx]!;

      // その境界セルの 4 隣接のうち空きマスを収集
      const emptyNeighbors: GridPos[] = [];
      const dirs: GridPos[] = [
        { x: origin.x - 1, y: origin.y },
        { x: origin.x + 1, y: origin.y },
        { x: origin.x, y: origin.y - 1 },
        { x: origin.x, y: origin.y + 1 },
      ];
      for (const n of dirs) {
        if (!inBounds(n, size)) continue;
        if (this.cells[gridIndex(n, size)] === 0) {
          emptyNeighbors.push(n);
        }
      }

      if (emptyNeighbors.length === 0) continue;

      // 空きマスからランダムに 1 つ選んで拡張
      const target = emptyNeighbors[Math.floor(Math.random() * emptyNeighbors.length)]!;
      const targetIdx = gridIndex(target, size);
      this.cells[targetIdx] = 1;

      // 波アニメを流用(spreadOrigin なし相当 = delay 0)
      this.animatingCells.push({ pos: target, age: 0 });

      // 次のティックでも使えるよう borderCells を更新(新規セルが境界になる可能性)
      const newDirs: GridPos[] = [
        { x: target.x - 1, y: target.y },
        { x: target.x + 1, y: target.y },
        { x: target.x, y: target.y - 1 },
        { x: target.x, y: target.y + 1 },
      ];
      let newIsBorder = false;
      for (const n of newDirs) {
        if (!inBounds(n, size)) continue;
        if (this.cells[gridIndex(n, size)] === 0) {
          newIsBorder = true;
          break;
        }
      }
      if (newIsBorder) {
        borderCells.push(target);
      }
    }

    this.redraw();
  }

  /** 複数マスを一括で領地化(enclose の結果を流し込む)。
   *  spreadOrigin を渡すと、その位置から波状に塗り広がる演出を再生。
   */
  addCells(positions: readonly GridPos[], spreadOrigin?: GridPos): void {
    if (positions.length === 0) return;

    let originX: number;
    let originY: number;
    if (spreadOrigin) {
      originX = spreadOrigin.x;
      originY = spreadOrigin.y;
    } else {
      let sx = 0;
      let sy = 0;
      for (const p of positions) {
        sx += p.x;
        sy += p.y;
      }
      originX = sx / positions.length;
      originY = sy / positions.length;
    }

    for (const pos of positions) {
      if (!inBounds(pos, this.grid.gridSize)) continue;
      const idx = gridIndex(pos, this.grid.gridSize);
      if (this.cells[idx] === 1) continue;
      this.cells[idx] = 1;

      const dist = Math.hypot(pos.x - originX, pos.y - originY);
      const delay = dist * WAVE_DELAY_PER_CELL;
      this.animatingCells.push({ pos, age: -delay });
    }

    // 領地拡張時の波紋(spreadOrigin がある時のみ発火、見た目だけの演出)
    if (spreadOrigin) {
      this.ripples.push({
        cx: (spreadOrigin.x + 0.5) * this.grid.cellPx,
        cy: (spreadOrigin.y + 0.5) * this.grid.cellPx,
        age: 0,
        duration: RIPPLE_DURATION_SEC,
        maxRadius: this.grid.cellPx * RIPPLE_RADIUS_CELLS,
      });
    }

    this.redraw();
  }

  /** 複数マスの所有権を放棄する(paper.io 式に他者へエリアを奪われた時)。
   *  即座に cells から外して再描画。視覚的な遷移は奪った側の addCells 波で補われる。 */
  removeCells(positions: readonly GridPos[]): void {
    let changed = false;
    for (const pos of positions) {
      if (!inBounds(pos, this.grid.gridSize)) continue;
      const idx = gridIndex(pos, this.grid.gridSize);
      if (this.cells[idx] === 1) {
        this.cells[idx] = 0;
        changed = true;
      }
    }
    if (changed) {
      // 奪われたセルが animatingCells に残っていると誤描画するので除去
      this.animatingCells = this.animatingCells.filter(
        (a) => this.cells[gridIndex(a.pos, this.grid.gridSize)] === 1,
      );
      this.redraw();
    }
  }

  /** 占領率(0..1)を返す。 */
  coverage(): number {
    let owned = 0;
    for (let i = 0; i < this.cells.length; i++) {
      if (this.cells[i] === 1) owned++;
    }
    return owned / this.cells.length;
  }

  /** トレイルが閉じた時、囲まれた領域を計算して返す。 */
  enclose(trailCells: readonly GridPos[]): GridPos[] {
    const size = this.grid.gridSize;
    const outside = new Uint8Array(size * size);
    const trailMask = new Uint8Array(size * size);

    for (const t of trailCells) {
      trailMask[gridIndex(t, size)] = 1;
    }

    const queue: GridPos[] = [];
    const seedFromBoundary = (pos: GridPos): void => {
      if (!inBounds(pos, size)) return;
      const idx = gridIndex(pos, size);
      if (outside[idx]) return;
      if (trailMask[idx]) return;
      if (this.cells[idx] === 1) return;
      outside[idx] = 1;
      queue.push(pos);
    };
    for (let i = 0; i < size; i++) {
      seedFromBoundary({ x: i, y: 0 });
      seedFromBoundary({ x: i, y: size - 1 });
      seedFromBoundary({ x: 0, y: i });
      seedFromBoundary({ x: size - 1, y: i });
    }

    while (queue.length > 0) {
      const head = queue.shift()!;
      const neighbors: GridPos[] = [
        { x: head.x - 1, y: head.y },
        { x: head.x + 1, y: head.y },
        { x: head.x, y: head.y - 1 },
        { x: head.x, y: head.y + 1 },
      ];
      for (const n of neighbors) {
        if (!inBounds(n, size)) continue;
        const idx = gridIndex(n, size);
        if (outside[idx]) continue;
        if (trailMask[idx]) continue;
        if (this.cells[idx] === 1) continue;
        outside[idx] = 1;
        queue.push(n);
      }
    }

    const enclosed: GridPos[] = [];
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const idx = y * size + x;
        if (!outside[idx] && this.cells[idx] === 0 && !trailMask[idx]) {
          enclosed.push({ x, y });
        }
      }
    }

    for (const t of trailCells) {
      enclosed.push(t);
    }

    return enclosed;
  }

  /** 領地全体の境界を 1 本(または複数)の滑らかな多角形として描画。
   *  - 各 grid edge(セル間の境界線)を方向付きエッジに変換
   *  - 同じ頂点から始まるエッジを連結して閉ループを構築
   *  - Chaikin の角切り平滑化を CHAIKIN_ITERATIONS 回適用
   *  - 結果を Pixi の Graphics.poly() で塗りつぶし
   */
  private drawSmoothBoundary(renderMask: Uint8Array, alpha: number): void {
    const size = this.grid.gridSize;
    const cellPx = this.grid.cellPx;

    const owned = (i: number, j: number): boolean => {
      if (i < 0 || i >= size || j < 0 || j >= size) return false;
      return renderMask[j * size + i] === 1;
    };

    // 方向付きエッジを集める(領地が進行方向の右側に来るように向き付け = 時計回り)
    const edges: number[][] = [];

    for (let j = 0; j <= size; j++) {
      for (let i = 0; i < size; i++) {
        const above = owned(i, j - 1);
        const below = owned(i, j);
        if (above && !below) {
          edges.push([i + 1, j, i, j]);
        } else if (!above && below) {
          edges.push([i, j, i + 1, j]);
        }
      }
    }

    for (let i = 0; i <= size; i++) {
      for (let j = 0; j < size; j++) {
        const left = owned(i - 1, j);
        const right = owned(i, j);
        if (left && !right) {
          edges.push([i, j, i, j + 1]);
        } else if (!left && right) {
          edges.push([i, j + 1, i, j]);
        }
      }
    }

    if (edges.length === 0) return;

    const keyFn = (x: number, y: number): number => x * (size + 2) + y;
    const fromMap = new Map<number, number[][]>();
    for (const e of edges) {
      const k = keyFn(e[0]!, e[1]!);
      const list = fromMap.get(k);
      if (list) {
        list.push(e);
      } else {
        fromMap.set(k, [e]);
      }
    }

    const visited = new Set<number[]>();
    const polygons: number[][] = [];

    for (const startEdge of edges) {
      if (visited.has(startEdge)) continue;

      const polygon: number[] = [];
      let curEdge: number[] | undefined = startEdge;

      while (curEdge && !visited.has(curEdge)) {
        visited.add(curEdge);
        polygon.push(curEdge[0]!, curEdge[1]!);

        const candidates = fromMap.get(keyFn(curEdge[2]!, curEdge[3]!));
        let nextEdge: number[] | undefined;
        if (candidates) {
          for (const c of candidates) {
            if (!visited.has(c)) {
              nextEdge = c;
              break;
            }
          }
        }
        curEdge = nextEdge;
      }

      if (polygon.length >= 6) {
        polygons.push(polygon);
      }
    }

    let drewSomething = false;
    for (const poly of polygons) {
      let pts = poly;
      for (let iter = 0; iter < CHAIKIN_ITERATIONS; iter++) {
        pts = this.chaikinSmooth(pts);
      }
      const pixelPts: number[] = new Array(pts.length);
      for (let i = 0; i < pts.length; i++) {
        pixelPts[i] = pts[i]! * cellPx;
      }
      this.graphics.poly(pixelPts);
      drewSomething = true;
    }

    if (drewSomething) {
      this.graphics.fill({ color: this.color, alpha });
    }
  }

  /** Chaikin's corner-cutting algorithm を 1 回適用。
   *  入力 N 頂点の閉多角形を 2N 頂点に増やし、各 90° の鋭角を滑らかに丸める。
   */
  private chaikinSmooth(pts: number[]): number[] {
    const n = pts.length;
    const result: number[] = new Array(n * 2);
    for (let i = 0; i < n; i += 2) {
      const x0 = pts[i]!;
      const y0 = pts[i + 1]!;
      const nextIdx = (i + 2) % n;
      const x1 = pts[nextIdx]!;
      const y1 = pts[nextIdx + 1]!;
      result[i * 2] = x0 * 0.75 + x1 * 0.25;
      result[i * 2 + 1] = y0 * 0.75 + y1 * 0.25;
      result[i * 2 + 2] = x0 * 0.25 + x1 * 0.75;
      result[i * 2 + 3] = y0 * 0.25 + y1 * 0.75;
    }
    return result;
  }

  private redraw(): void {
    this.graphics.clear();
    const size = this.grid.gridSize;

    const animByCell = new Map<number, AnimatingCell>();
    for (const a of this.animatingCells) {
      animByCell.set(gridIndex(a.pos, size), a);
    }

    const visibleMask = new Uint8Array(size * size);
    const freshMask = new Uint8Array(size * size);
    let freshTotalT = 0;
    let freshCount = 0;

    for (let i = 0; i < size * size; i++) {
      if (this.cells[i] !== 1) continue;
      const anim = animByCell.get(i);
      if (!anim) {
        visibleMask[i] = 1;
      } else if (anim.age >= 0) {
        visibleMask[i] = 1;
        freshMask[i] = 1;
        freshTotalT += Math.min(1, anim.age / FADE_DURATION_SEC);
        freshCount++;
      }
    }

    // Pass 1: 表示可能な領地全体を 1 本の滑らかな境界として描画
    this.drawSmoothBoundary(visibleMask, STABLE_ALPHA);

    // Pass 2: 新規セル(波が届いて間もない)に追加の明るさを重ねてフェード
    if (freshCount > 0) {
      const avgT = freshTotalT / freshCount;
      const extraAlpha = (TRAIL_BRIGHT_ALPHA - STABLE_ALPHA) * (1 - avgT);
      if (extraAlpha > 0.01) {
        this.drawSmoothBoundary(freshMask, extraAlpha);
      }
    }

    // Pass 3: 波紋リング(spreadOrigin から広がる、塗料が紙に染み込む瞬間の余韻)
    for (const r of this.ripples) {
      const t = Math.min(1, r.age / r.duration);
      const easeOut = 1 - (1 - t) * (1 - t);
      const radius = easeOut * r.maxRadius;
      const alpha = Math.pow(1 - t, 1.25) * 0.32;
      if (alpha <= 0.005 || radius <= 0) continue;
      this.graphics.circle(r.cx, r.cy, radius);
      this.graphics.stroke({ width: Math.max(1.4, this.grid.cellPx * 0.075) * (1 - t * 0.35), color: this.color, alpha });
      // ごく薄い内側ハロー
      this.graphics.circle(r.cx, r.cy, radius * 0.78);
      this.graphics.stroke({ width: Math.max(1, this.grid.cellPx * 0.035), color: 0xffffff, alpha: alpha * 0.34 });
      this.graphics.circle(r.cx, r.cy, radius * 0.52);
      this.graphics.fill({ color: this.color, alpha: alpha * 0.06 });
    }
  }
}
